import { API_BASE, authHeaders } from './apiAuth'

/**
 * Monthly allowances (Huanrui, 2026-10-19; workers/api/src/allowance.ts)
 * -------------------------------------------------------------
 * What a person may spend each month in credits (1,000 = $1), what is left,
 * and asking for more; and, for administrators, setting them.
 */

export interface MyAllowance {
  /** Credits a month; null: no limit. */
  limit: number | null
  used: number
  /** Null: no limit. */
  left: number | null
  from: 'own' | 'everyone' | 'none'
  month: string
  /** Asked for more, not yet answered. */
  asked: boolean
}

export interface AllowancePerson {
  email: string
  name: string
  /** Their own allowance, when one is set (null: no limit for them). */
  own?: number | null
  hasOwn: boolean
  limit: number | null
  from: 'own' | 'everyone' | 'none'
  used: number
  left: number | null
}

export interface AllowanceRequest {
  id: string
  email: string
  name: string
  askedAt: string
  note: string
  used: number
  allowance: number | null
  resolvedAt: string | null
  outcome: 'raised' | 'declined' | null
  usedNow: number
}

export interface Allowances {
  /** Everybody's monthly allowance; null: none. */
  everyone: number | null
  people: AllowancePerson[]
  requests: AllowanceRequest[]
}

async function call<T>(path: string, init?: { method?: string; body?: unknown }): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: init?.method ?? 'GET',
    headers: {
      Accept: 'application/json',
      ...(init?.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(await authHeaders()),
    },
    ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  })
  if (!response.ok) {
    const said = (await response.json().catch(() => null)) as { error?: string } | null
    throw new Error(said?.error || `That could not be done (HTTP ${response.status})`)
  }
  return (await response.json()) as T
}

export const readMyAllowance = () => call<MyAllowance>('/api/me/allowance')
export const askForMore = (note = '') => call<{ ok: boolean; already?: boolean }>('/api/me/allowance/request', { method: 'POST', body: { note } })

export const readAllowances = () => call<Allowances>('/api/admin/allowances')
/** One person's (or '*' for everybody's) monthly allowance; null: no limit. */
export const setAllowance = (email: string, credits: number | null) =>
  call<{ ok: boolean }>('/api/admin/allowances', { method: 'POST', body: { email, credits } })
/** A person back to everybody's; '*' back to none. */
export const clearAllowance = (email: string) =>
  call<{ ok: boolean }>('/api/admin/allowances', { method: 'POST', body: { email, clear: true } })
export const resolveRequest = (id: string, outcome: 'raised' | 'declined', credits?: number | null) =>
  call<{ ok: boolean }>('/api/admin/allowances/resolve', { method: 'POST', body: { id, outcome, credits } })

/** Said after something that spends, so the bar's number is read again. */
export function allowanceMayHaveChanged(): void {
  window.dispatchEvent(new Event('ww-allowance'))
}

/** 12,345 */
export const credits = (n: number): string => Math.round(n).toLocaleString('en-US')
